import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { registerUser, getUserCredits, getUserProfile } from '../services/userService';
import { getProfileSnoozeUntil } from '../utils/profileSnooze';

interface UserProfileData {
  userId: string;
  emailId: string;
  name: string;
  profileUrl: string;
  companyName: string;
  phoneNumber: string;
  registrationDate: string;
  credits: number; // Float value
}

interface MissingProfileFields {
  companyName: boolean;
  phoneNumber: boolean;
}

interface AuthContextType {
  currentUser: FirebaseUser | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithGoogle: () => Promise<boolean>;
  signup: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  loading: boolean;
  userCredits: number; // Float value
  refreshUserCredits: () => Promise<void>;
  userProfile: UserProfileData | null;
  updateUserProfile: (updates: Partial<UserProfileData>) => void;
  refreshUserProfile: () => Promise<void>;
  showProfileCompletion: boolean;
  setShowProfileCompletion: (show: boolean) => void;
  missingProfileFields: MissingProfileFields | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [userCredits, setUserCredits] = useState<number>(0); // Default to 5 credits
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);
  const [showProfileCompletion, setShowProfileCompletion] = useState<boolean>(false);
  const [missingProfileFields, setMissingProfileFields] = useState<MissingProfileFields | null>(null);

  // Fetch user credits
  const fetchUserCredits = async (userId: string) => {
    try {
      const { credits } = await getUserCredits(userId);
      setUserCredits(credits);
    } catch (error) {
      console.error('Failed to fetch user credits:', error);
      // Keep the default value
    }
  };

  // Function to manually refresh user credits
  const refreshUserCredits = async () => {
    if (currentUser) {
      await fetchUserCredits(currentUser.uid);
    }
  };

  // Fetch user profile from backend
  const fetchUserProfile = async (userId: string, checkCompletion: boolean = false) => {
    try {
      const response = await getUserProfile(userId);
      if (response.success) {
        setUserProfile(response.data);
        setUserCredits(response.data.credits);

        // Check if profile completion modal should be shown
        if (checkCompletion) {
          const missingFields: MissingProfileFields = {
            companyName: !response.data.companyName || response.data.companyName.trim() === '',
            phoneNumber: !response.data.phoneNumber || response.data.phoneNumber.trim() === ''
          };

          const hasAnyMissingFields = missingFields.companyName || missingFields.phoneNumber;
          setMissingProfileFields(missingFields);

          // Gate the modal with local snooze timer (per user)
          if (hasAnyMissingFields) {
            const snoozeUntil = getProfileSnoozeUntil(userId);
            const shouldShow = !snoozeUntil || Date.now() >= snoozeUntil;
            setShowProfileCompletion(shouldShow);
          } else {
            setShowProfileCompletion(false);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  // Function to manually refresh user profile
  const refreshUserProfile = async () => {
    if (currentUser) {
      await fetchUserProfile(currentUser.uid);
    }
  };

  // Function to update user profile locally
  const updateUserProfile = (updates: Partial<UserProfileData>) => {
    if (userProfile) {
      setUserProfile({ ...userProfile, ...updates });
    }
  };

  // Register user with backend
  const registerUserWithBackend = useCallback(async (user: FirebaseUser) => {
    try {
      await registerUser({
        userId: user.uid,
        emailId: user.email || '',
        name: user.displayName || user.email?.split('@')[0] || 'Anonymous User',
        profileUrl: user.photoURL || '',
      });
      console.log('User registered with backend successfully');

      // Fetch user profile (includes credits) after registration
      // Check completion for new Google OAuth users
      const isNewGoogleUser = user.providerData.some(provider => provider.providerId === 'google.com');
      await fetchUserProfile(user.uid, isNewGoogleUser);
    } catch (error) {
      console.error('Failed to register user with backend:', error);
      // Continue execution even if backend registration fails
    }
  }, []);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsAuthenticated(!!user);

      // Register user with backend when they authenticate and fetch profile
      if (user) {
        registerUserWithBackend(user);
      } else {
        // Clear profile data when user logs out
        setUserProfile(null);
        setShowProfileCompletion(false);
        setMissingProfileFields(null);
      }

      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [registerUserWithBackend]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      const result = await signInWithEmailAndPassword(auth, email, password);
      if (result.user) {
        await registerUserWithBackend(result.user);
      }
      return true;
    } catch (error) {
      console.error('Firebase login failed:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async (): Promise<boolean> => {
    try {
      setLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      if (result.user) {
        await registerUserWithBackend(result.user);
      }
      return true;
    } catch (error) {
      console.error('Google sign-in failed:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      if (result.user) {
        await registerUserWithBackend(result.user);
      }
      return true;
    } catch (error) {
      console.error('Signup failed:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setLoading(true);
      await signOut(auth);
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    isAuthenticated,
    login,
    loginWithGoogle,
    signup,
    logout,
    loading,
    userCredits,
    refreshUserCredits,
    userProfile,
    updateUserProfile,
    refreshUserProfile,
    showProfileCompletion,
    setShowProfileCompletion,
    missingProfileFields
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 